import jwt_decode from "jwt-decode";

export interface AccessTokenPayload {
  exp?: number;
  unique_name?: string;
}

export function isAuthEnabled(): boolean {
  return process.env.REACT_APP_AUTH_ENABLED !== "false";
}

export function getStoredAccessToken(): string | null {
  const raw =
    localStorage.getItem("accessToken") || localStorage.getItem("access_token");
  const token = raw?.trim();
  return token || null;
}

export function decodeAccessToken(token: string): AccessTokenPayload | null {
  try {
    return jwt_decode<AccessTokenPayload>(token);
  } catch {
    return null;
  }
}

/** Returns true when the token is missing, invalid, or past exp (with optional clock skew). */
export function isAccessTokenExpired(
  token: string,
  skewSeconds = 30
): boolean {
  const payload = decodeAccessToken(token);
  if (!payload) {
    return true;
  }
  if (!payload.exp) {
    return false;
  }
  return payload.exp * 1000 <= Date.now() + skewSeconds * 1000;
}

export function clearAuthSession(): void {
  localStorage.clear();
  sessionStorage.clear();
}

export function isUnauthorizedHttpStatus(status: number | undefined): boolean {
  return status === 401 || status === 403;
}

/** Clears session and hard-redirects to login (safe from axios interceptors). */
export function redirectToLogin(): void {
  clearAuthSession();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
}

/** True when a stored token exists and is still valid. Clears storage if expired/invalid. */
export function hasValidStoredSession(): boolean {
  if (!isAuthEnabled()) {
    return Boolean(getStoredAccessToken());
  }
  const token = getStoredAccessToken();
  if (!token) {
    return false;
  }
  if (isAccessTokenExpired(token)) {
    clearAuthSession();
    return false;
  }
  return true;
}
